import { formatPersonFullName } from "../../../utils/formatters";
import type { SuspectRecord } from "../../../utils/nurseMergeModels";
import type { HeldCellValue, NurseResult } from "../hooks";
import type { DepartmentRolesUpdate } from "./NursesSummary";

/** `POST /scripts/roster/add_to_department` — one existing record per held row. */
export type AddToDepartmentItem = { nurse_id: number; email?: string; phone_number?: string };

export type AddToDepartmentItemResult = {
	nurse_id: number;
	status: "added" | "already_in_department" | "error";
	reason: string;
	contact_filled: Array<"email" | "phone_number">;
};

export type AddToDepartmentResponse = { results: AddToDepartmentItemResult[] };

/** `POST /scripts/roster/create_held` — the roster upload's own response shape. */
export type CreateHeldResponse = {
	results: NurseResult[];
	summary: { total: number; success: number; held: number; failed: number };
	is_update: boolean;
	/** null when the roles config could not be updated after the records were created — see `warnings`. */
	department_roles_update: DepartmentRolesUpdate | null;
	/** What went wrong after the records were committed; empty when nothing did. */
	warnings: string[];
};

/** A result row the loader held: not created, waiting for the onboarder. */
export type HeldResult = NurseResult & {
	action: "held";
	first_name: string;
	last_name: string;
	held_candidates: SuspectRecord[];
	held_row: Record<string, HeldCellValue>;
};

/** One held row, by its position in the upload results. */
export type HeldSource = { index: number; row: HeldResult };

/** One grid row: a held row and one of the existing records with its name. */
export type HeldReviewRow = {
	/** `<result index>-<candidate id>` — a record can be a candidate for two rows. */
	key: string;
	source: HeldSource;
	uploadedName: string;
	candidate: SuspectRecord;
	/** The first candidate's row carries the held row's own action ("Create separate record"). */
	firstOfSource: boolean;
};

/** What an action did to a held row. */
export type HeldOutcome = { kind: "added" | "already" | "created" | "error"; reason: string };

const isHeld = (row: NurseResult): row is HeldResult =>
	row.action === "held" && row.held_row !== undefined && row.held_candidates !== undefined && row.first_name !== undefined && row.last_name !== undefined;

export const heldSources = (results: NurseResult[]): HeldSource[] => results.flatMap((row, index) => (isHeld(row) ? [{ index, row }] : []));

export const heldReviewRows = (results: NurseResult[], lastNameFirst: boolean): HeldReviewRow[] =>
	heldSources(results).flatMap((source) =>
		source.row.held_candidates.map((candidate, candidateIndex) => ({
			key: `${source.index}-${candidate.nurseId}`,
			source,
			uploadedName: formatPersonFullName({ firstName: source.row.first_name, lastName: source.row.last_name }, lastNameFirst),
			candidate,
			firstOfSource: candidateIndex === 0,
		})),
	);

/** A cell's text, or nothing for an empty cell — a phone typed as a number arrives as one. */
const cellText = (value: HeldCellValue | undefined): string | undefined => {
	if (typeof value === "number") return String(value);
	if (typeof value === "string" && value.trim()) return value.trim();
	return undefined;
};

/** The row's own email and phone, as uploaded (absent when the cell was empty). */
export const rowContact = (source: HeldSource): { email?: string; phone?: string } => ({
	email: cellText(source.row.held_row.email),
	phone: cellText(source.row.held_row.phone_number),
});

/** The add request for a held row: the record chosen, plus the row's email/phone to fill where the record has none. */
export const addItemFor = (source: HeldSource, candidate: SuspectRecord): AddToDepartmentItem => {
	const item: AddToDepartmentItem = { nurse_id: candidate.nurseId };
	const { email, phone } = rowContact(source);
	if (email) item.email = email;
	if (phone) item.phone_number = phone;
	return item;
};

/** US numbers compared by their last ten digits — the row may say `(440) 649-0010` where the record stores `+14406490010`. */
const NATIONAL_DIGITS = 10;
const nationalDigits = (phone: string): string => phone.replace(/\D/g, "").slice(-NATIONAL_DIGITS);

/** The row carries an email or phone that differs from the one the record already has — a sign it may not be them. */
export const contactConflicts = (source: HeldSource, candidate: SuspectRecord): boolean => {
	const { email, phone } = rowContact(source);
	const emailDiffers = email !== undefined && candidate.email !== null && email.toLowerCase() !== candidate.email.toLowerCase();
	const phoneDiffers = phone !== undefined && candidate.phoneNumber !== null && nationalDigits(phone) !== nationalDigits(candidate.phoneNumber);
	return emailDiffers || phoneDiffers;
};

/**
 * Held rows the bulk "add" may resolve without asking: exactly one existing record, no contact on the row that
 * contradicts it, a record no other held row also names (adding it twice in one request acts on stale data), and
 * not already resolved. A row whose last action failed may be retried.
 */
export const bulkAddSources = (sources: HeldSource[], outcomes: ReadonlyMap<number, HeldOutcome>): HeldSource[] => {
	const namedBy = new Map<number, number>();
	for (const source of sources) {
		// DEFAULT: 0 — a record not counted yet.
		for (const candidate of source.row.held_candidates) namedBy.set(candidate.nurseId, (namedBy.get(candidate.nurseId) ?? 0) + 1);
	}
	return sources.filter((source) => {
		const [only, ...more] = source.row.held_candidates;
		const outcome = outcomes.get(source.index);
		return more.length === 0 && namedBy.get(only.nurseId) === 1 && !contactConflicts(source, only) && (!outcome || outcome.kind === "error");
	});
};

const ADD_KIND: Record<AddToDepartmentItemResult["status"], HeldOutcome["kind"]> = { added: "added", already_in_department: "already", error: "error" };

/** The add results, which come back in request order, keyed by the held row each item came from. */
export const addOutcomes = (sources: HeldSource[], response: AddToDepartmentResponse): Map<number, HeldOutcome> =>
	new Map(response.results.map((result, i) => [sources[i].index, { kind: ADD_KIND[result.status], reason: result.reason }]));

/** The create results, one per row sent and in the same order, keyed by the held row. */
export const createOutcomes = (sources: HeldSource[], response: CreateHeldResponse): Map<number, HeldOutcome> =>
	new Map(
		response.results.map((result, i) => {
			// A row whose contact now matches a record is linked to it instead of created — still resolved.
			const kind: HeldOutcome["kind"] = result.success !== true ? "error" : result.action === "linked" ? "added" : "created";
			return [sources[i].index, { kind, reason: result.reason }];
		}),
	);

/** Counts for the outcome line under the grid. */
export const outcomeCounts = (outcomes: ReadonlyMap<number, HeldOutcome>) => {
	const counts = { added: 0, created: 0, failed: 0 };
	for (const outcome of outcomes.values()) {
		if (outcome.kind === "error") counts.failed += 1;
		else if (outcome.kind === "created") counts.created += 1;
		else counts.added += 1;
	}
	return counts;
};
